// src/components/profile.jsx — FINAL ESDRAS USER PROFILE (With Logout)
import React, { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { signOut } from 'firebase/auth'; // Added for Logout
import { db, auth } from '../firebase'; // Using your centralized auth
import { useNavigate } from 'react-router-dom';

const NAVY = '#001F3F';
const GOLD = '#B8860B';
const RED = '#ff4d4d'; // Color for logout

export default function Profile() {
  const [userData, setUserData] = useState({});
  const [loading, setLoading] = useState(true);

  const user = auth.currentUser;
  const navigate = useNavigate();

  useEffect(() => {
    // If no user is found in the current session
    if (!user) {
      navigate('/');
      return;
    }

    const unsub = onSnapshot(doc(db, 'users', user.uid), (snap) => {
      if (snap.exists()) {
        setUserData(snap.data());
      }
      setLoading(false);
    }, (err) => {
      console.error('Profile load error:', err);
      setLoading(false);
    });

    return unsub;
  }, [user, navigate]);

  // Handle Logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/'); // Redirect to login/home page
    } catch (err) {
      console.error('Logout failed:', err);
      alert('Failed to log out. Please try again.');
    }
  };

  if (!user || loading) {
    return (
      <div style={{ minHeight: '100vh', background: NAVY, color: GOLD, display: 'grid', placeItems: 'center', fontFamily: 'Montserrat, sans-serif', fontSize: '1.8rem', fontWeight: '700' }}>
        {loading ? 'loading profile...' : 'redirecting...'}
      </div>
    );
  }

  const isPremium = userData.subscription === 'premium_yearly';
  const stylesUsed = userData.stylesUsed || 0;
  const freeRemaining = Math.max(0, 10 - stylesUsed);
  const extraPreviews = userData.extraPreviews || 0;
  const totalAvailable = isPremium ? 'unlimited' : freeRemaining + extraPreviews;

  const base = (user.displayName || user.email?.split('@')[0] || 'KING').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const referralCode = base.slice(0, 8) || user.uid.slice(0, 8).toUpperCase();
  const referralLink = `https://esdras.app/?ref=${user.uid}`;

  const handleShare = async () => {
    const shareText = `Try ESDRAS – see any hairstyle on your exact head in seconds! Use my code ${referralCode} for 3 extra premium previews 👑\n${referralLink}`;
    if (navigator.share) {
      try { await navigator.share({ title: 'ESDRAS – Precision Grooming', text: shareText }); } catch (err) { }
    } else {
      navigator.clipboard.writeText(shareText);
      alert('Referral link copied to clipboard!');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: NAVY, color: 'white', fontFamily: 'Montserrat, sans-serif', padding: '2rem 1rem' }}>
      
      {/* Logo */}
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <img src="/icons/esdras-logo.png" alt="Esdras logo" style={{ width: '120px', height: '120px', objectFit: 'contain' }} />
      </div>

      <h1 style={{ textAlign: 'center', color: GOLD, fontSize: '2.5rem', fontWeight: '800', margin: '0 0 0.5rem' }}>my profile</h1>
      <p style={{ textAlign: 'center', opacity: 0.9, fontSize: '1.3rem', marginBottom: '2rem' }}>welcome back, {user.displayName || 'king'} 👑</p>

      {/* Premium Badge */}
      {isPremium && (
        <div style={{ textAlign: 'center', margin: '1rem auto', maxWidth: '500px', padding: '1rem', background: 'rgba(184, 134, 11, 0.2)', borderRadius: '20px', border: `2px solid ${GOLD}` }}>
          <p style={{ margin: 0, fontSize: '1.2rem', color: GOLD, fontWeight: '700' }}>Premium Member • Unlimited Styles 👑</p>
        </div>
      )}

      {/* Free Try-On Counter */}
      {!isPremium && (
        <div style={{ maxWidth: '500px', margin: '2rem auto', background: 'rgba(255, 255, 255, 0.05)', padding: '2rem', borderRadius: '20px', border: `1px solid rgba(184, 134, 11, 0.3)` }}>
          <p style={{ margin: '0 0 1rem', fontSize: '1.2rem', color: GOLD, fontWeight: '700' }}>your try-ons</p>
          <div style={{ background: '#0a3d62', height: '50px', borderRadius: '25px', overflow: 'hidden', position: 'relative' }}>
            <div style={{ width: `${Math.min(100, (stylesUsed / 10) * 100)}%`, background: GOLD, height: '100%', transition: 'width 0.8s ease' }} />
            <p style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', margin: 0, fontSize: '1.1rem', fontWeight: 'bold' }}>
              {freeRemaining} free + {extraPreviews} bonus
            </p>
          </div>
        </div>
      )}

      {/* Referral Section */}
      <div style={{ maxWidth: '500px', margin: '2rem auto', background: 'rgba(255, 255, 255, 0.05)', padding: '2rem', borderRadius: '24px', border: `2px solid ${GOLD}`, textAlign: 'center' }}>
        <h2 style={{ color: GOLD, margin: '0 0 1rem', fontSize: '1.8rem' }}>refer & earn</h2>
        <div style={{ background: 'white', color: NAVY, padding: '1rem', borderRadius: '12px', margin: '1rem 0', fontWeight: 'bold', fontSize: '1.5rem', letterSpacing: '4px' }}>
          {referralCode}
        </div>
        <button onClick={handleShare} style={{ width: '100%', background: GOLD, color: 'black', padding: '1.2rem', border: 'none', borderRadius: '50px', fontSize: '1.2rem', fontWeight: 'bold', cursor: 'pointer' }}>
          share & earn previews
        </button>
      </div>

      {/* Logout Button */}
      <div style={{ textAlign: 'center', marginTop: '4rem', marginBottom: '2rem' }}>
        <button
          onClick={handleLogout}
          style={{
            background: 'transparent',
            color: RED,
            border: `2px solid ${RED}`,
            padding: '1rem 3.5rem',
            borderRadius: '50px',
            fontSize: '1.1rem',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: '0.3s'
          }}
          onMouseEnter={(e) => (e.target.style.background = 'rgba(255, 77, 77, 0.1)')}
          onMouseLeave={(e) => (e.target.style.background = 'transparent')}
        >
          LOG OUT
        </button>
      </div>

      {/* Upgrade CTA (non-premium only) */}
      {!isPremium && (
        <div style={{ textAlign: 'center' }}>
          <button onClick={() => navigate('/checkout')} style={{ background: 'none', border: 'none', color: GOLD, textDecoration: 'underline', cursor: 'pointer', opacity: 0.7 }}>
            upgrade to premium for ₦15,000/year
          </button>
        </div>
      )}
    </div>
  );
                     }
                                
